import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types & state factory
import {
    type Task,
    type Note,
    type TeamCheckin,
    type TeamMember,
    type TeamSessionState,
    type TeamSessionsRootState,
    type ApiTeamFocusSession,
    type TeamFocusSessionParticipant,
    createInitialTeamSessionState,
} from "./types";

// Domain thunks
import { createTeamTask, updateTeamTask, fetchTeamTasks, deleteTeamTask } from "./taskThunks";
import {
    fetchTeamNotes,
    createTeamNote,
    updateTeamNote,
    deleteTeamNote,
} from "./noteThunks";
import {
    fetchTeamCheckins,
    createTeamCheckin,
    updateTeamCheckin,
    deleteTeamCheckin,
} from "./checkinThunks";
import { fetchTeamSession, fetchRtcToken } from "./sessionThunks";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize raw API/preloaded data to the canonical field names used in the
 * Redux slice.  The backend may return timer fields under slightly different
 * names (e.g. `timerStartTime` instead of `timerStartedAt`), so we coerce
 * them here before spreading into the store so that `syncTimerState` can
 * always rely on the expected property names.
 */
const normalizeSessionData = (raw: any): any => {
    if (!raw || typeof raw !== 'object') return raw;
    const out = { ...raw };

    // Map API participants -> teamMembers
    if (raw.participants && Array.isArray(raw.participants)) {
        out.teamMembers = raw.participants.map((p: TeamFocusSessionParticipant) => ({
            id: p.userId,
            name: p.userId || 'Unknown',
            avatar: (p.userId || 'U').charAt(0).toUpperCase(),
            isOnline: p.status === 'active' && !p.leftAt,
            isMuted: true, // Default
        }));
    }

    // Duration fallback (API may return null)
    if (out.duration === undefined || out.duration === null) {
        out.duration = 25; // default 25 minutes
    }

    return out;
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const initialState: TeamSessionsRootState = { sessions: {} };

const teamFocusSessionSlice = createSlice({
    name: "teamFocusSession",
    initialState,
    reducers: {
        initTeamSession: (
            state,
            action: PayloadAction<{
                teamId: string;
                initialData?: Partial<TeamSessionState>;
            }>,
        ) => {
            const { teamId, initialData } = action.payload;
            if (!state.sessions[teamId]) {
                const s = {
                    ...createInitialTeamSessionState(),
                    ...normalizeSessionData(initialData),
                };
                state.sessions[teamId] = s;
            }
        },

        updateTeamSessionState: (
            state,
            action: PayloadAction<{
                teamId: string;
                updates: Partial<TeamSessionState>;
            }>,
        ) => {
            const { teamId, updates } = action.payload;
            if (state.sessions[teamId]) {
                const session = state.sessions[teamId];
                Object.assign(session, updates);
            }
        },

        toggleTask: (
            state,
            action: PayloadAction<{ teamId: string; taskId: string }>,
        ) => {
            const { teamId, taskId } = action.payload;
            const session = state.sessions[teamId];
            if (session) {
                const t = session.tasks.find((t) => t.id === taskId);
                if (t) {
                    t.status = t.status === "done" ? "todo" : "done";
                    t.completed = t.status === "done";
                }
            }
        },

        addTask: (
            state,
            action: PayloadAction<{ teamId: string; task: Task }>,
        ) => {
            const { teamId, task } = action.payload;
            if (state.sessions[teamId]) {
                // Avoid duplicates (e.g. if the creator already pushed via thunk)
                const exists = state.sessions[teamId].tasks.some(
                    (t) => t.id === task.id,
                );
                if (!exists) {
                    state.sessions[teamId].tasks.push(task);
                }
            }
        },

        editTask: (
            state,
            action: PayloadAction<{
                teamId: string;
                taskId: string;
                updates: Partial<Task>;
            }>,
        ) => {
            const { teamId, taskId, updates } = action.payload;
            const session = state.sessions[teamId];
            if (session) {
                const index = session.tasks.findIndex((t) => t.id === taskId);
                if (index !== -1) {
                    session.tasks[index] = { ...session.tasks[index], ...updates };
                }
            }
        },

        deleteTask: (
            state,
            action: PayloadAction<{ teamId: string; taskId: string }>,
        ) => {
            const { teamId, taskId } = action.payload;
            if (state.sessions[teamId]) {
                state.sessions[teamId].tasks = state.sessions[teamId].tasks.filter(
                    (t) => t.id !== taskId,
                );
            }
        },

        // -- Notes --
        addNote: (
            state,
            action: PayloadAction<{ teamId: string; note: Note }>,
        ) => {
            const { teamId, note } = action.payload;
            if (state.sessions[teamId]) {
                const exists = state.sessions[teamId].notes.some(n => n.id === note.id);
                if (!exists) {
                    state.sessions[teamId].notes.push(note);
                }
            }
        },

        editNote: (
            state,
            action: PayloadAction<{ teamId: string; noteId: string; text: string }>,
        ) => {
            const { teamId, noteId, text } = action.payload;
            const session = state.sessions[teamId];
            if (session) {
                const n = session.notes.find((n) => n.id === noteId);
                if (n) n.note = text;
            }
        },

        deleteNote: (
            state,
            action: PayloadAction<{ teamId: string; noteId: string }>,
        ) => {
            const { teamId, noteId } = action.payload;
            if (state.sessions[teamId]) {
                state.sessions[teamId].notes = state.sessions[teamId].notes.filter(
                    (n) => n.id !== noteId,
                );
            }
        },

        // -- Members --
        addMember: (
            state,
            action: PayloadAction<{ teamId: string; member: TeamMember }>,
        ) => {
            const { teamId, member } = action.payload;
            if (state.sessions[teamId]) {
                state.sessions[teamId].teamMembers.push(member);
            }
        },

        removeMember: (
            state,
            action: PayloadAction<{ teamId: string; memberId: string }>,
        ) => {
            const { teamId, memberId } = action.payload;
            if (state.sessions[teamId]) {
                state.sessions[teamId].teamMembers = state.sessions[
                    teamId
                ].teamMembers.filter((m) => m.id !== memberId);
            }
        },

        toggleMemberMute: (
            state,
            action: PayloadAction<{
                teamId: string;
                memberId: string;
                isMuted: boolean;
            }>,
        ) => {
            const { teamId, memberId, isMuted } = action.payload;
            const session = state.sessions[teamId];
            if (session) {
                const m = session.teamMembers.find((m) => m.id === memberId);
                if (m) m.isMuted = isMuted;
            }
        },

        // -- Check-ins (optimistic helpers) --
        addCheckin: (
            state,
            action: PayloadAction<{ teamId: string; checkin: TeamCheckin }>,
        ) => {
            const { teamId, checkin } = action.payload;
            if (state.sessions[teamId]) {
                const exists = state.sessions[teamId].checkins.some(c => c.id === checkin.id);
                if (!exists) {
                    state.sessions[teamId].checkins.push(checkin);
                }
            }
        },

        updateCheckin: (
            state,
            action: PayloadAction<{
                teamId: string;
                checkinId: string;
                updates: Partial<TeamCheckin>;
            }>,
        ) => {
            const { teamId, checkinId, updates } = action.payload;
            const session = state.sessions[teamId];
            if (session) {
                const c = session.checkins.find((c) => c.id === checkinId);
                if (c) Object.assign(c, updates);
            }
        },

        deleteCheckin: (
            state,
            action: PayloadAction<{ teamId: string; checkinId: string }>,
        ) => {
            const { teamId, checkinId } = action.payload;
            if (state.sessions[teamId]) {
                state.sessions[teamId].checkins = state.sessions[
                    teamId
                ].checkins.filter((c) => c.id !== checkinId);
            }
        },
    },

    // -------------------------------------------------------------------------
    // Extra reducers (async thunk side-effects)
    // -------------------------------------------------------------------------
    extraReducers: (builder) => {
        // -- Tasks --
        builder.addCase(updateTeamTask.fulfilled, (state, action) => {
            const { teamId, taskId, updates } = action.payload;
            const session = state.sessions[teamId];
            if (session) {
                const idx = session.tasks.findIndex((t) => t.id === taskId);
                if (idx !== -1) {
                    session.tasks[idx] = { ...session.tasks[idx], ...updates };
                }
            }
        });

        builder.addCase(createTeamTask.fulfilled, (state, action) => {
            const { teamId, task } = action.payload;
            if (state.sessions[teamId]) {
                const exists = state.sessions[teamId].tasks.some((t) => t.id === task.id);
                if (!exists) {
                    state.sessions[teamId].tasks.push(task);
                }
            }
        });

        builder.addCase(deleteTeamTask.fulfilled, (state, action) => {
            const { teamId, taskId } = action.payload;
            if (state.sessions[teamId]) {
                state.sessions[teamId].tasks = state.sessions[teamId].tasks.filter(
                    (t) => t.id !== taskId,
                );
            }
        });

        builder
            .addCase(fetchTeamTasks.pending, (state, action) => {
                const teamId = action.meta.arg;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].tasksLoading = true;
                state.sessions[teamId].tasksError = null;
            })
            .addCase(fetchTeamTasks.fulfilled, (state, action) => {
                const { teamId, data } = action.payload;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].tasks = data;
                state.sessions[teamId].tasksLoading = false;
                state.sessions[teamId].tasksError = null;
            })
            .addCase(fetchTeamTasks.rejected, (state, action) => {
                const teamId = action.meta.arg;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].tasksLoading = false;
                state.sessions[teamId].tasksError =
                    (action.payload as string) || "Failed to fetch tasks";
            });

        // -- Session --
        builder
            .addCase(fetchTeamSession.pending, (_state, _action) => {
                // per-team loading could be tracked here if needed
            })
            .addCase(fetchTeamSession.fulfilled, (state, action) => {
                const { teamId, data } = action.payload;
                const normalized = normalizeSessionData(data || {});

                if (!state.sessions[teamId]) {
                    state.sessions[teamId] = {
                        ...createInitialTeamSessionState(),
                        ...normalized,
                    };
                } else if (data) {
                    // Preserve locally managed properties
                    const existingRtcToken = state.sessions[teamId].rtcToken;
                    const existingNotes = state.sessions[teamId].notes;
                    const existingCheckins = state.sessions[teamId].checkins;
                    const existingTasks = state.sessions[teamId].tasks;
                    const existingReminders = state.sessions[teamId].reminders;
                    const existingTeamMembers = state.sessions[teamId].teamMembers;

                    state.sessions[teamId] = { ...state.sessions[teamId], ...normalized };

                    if (existingRtcToken)
                        state.sessions[teamId].rtcToken = existingRtcToken;
                    if (existingNotes && existingNotes.length > 0)
                        state.sessions[teamId].notes = existingNotes;
                    if (existingCheckins && existingCheckins.length > 0)
                        state.sessions[teamId].checkins = existingCheckins;
                    if (existingTasks && existingTasks.length > 0)
                        state.sessions[teamId].tasks = existingTasks;
                    if (existingReminders && existingReminders.length > 0)
                        state.sessions[teamId].reminders = existingReminders;
                    if (existingTeamMembers && existingTeamMembers.length > 0)
                        state.sessions[teamId].teamMembers = existingTeamMembers;
                }

                // Safety: ensure arrays are never overwritten with undefined/objects
                const s = state.sessions[teamId];
                if (!Array.isArray(s.notes)) s.notes = [];
                if (!Array.isArray(s.tasks)) s.tasks = [];
                if (!Array.isArray(s.reminders)) s.reminders = [];
                if (!Array.isArray(s.checkins)) s.checkins = [];
                if (!Array.isArray(s.teamMembers)) s.teamMembers = [];
                if (!Array.isArray(s.participants)) s.participants = [];
            })
            .addCase(fetchTeamSession.rejected, (_state, _action) => {
                // component handles invalid session
            });

        // -- Notes --
        builder
            .addCase(fetchTeamNotes.pending, (state, action) => {
                const teamId = action.meta.arg.teamId;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].notesLoading = true;
            })
            .addCase(fetchTeamNotes.fulfilled, (state, action) => {
                const { teamId, data } = action.payload;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].notes = data;
                state.sessions[teamId].notesLoading = false;
            })
            .addCase(fetchTeamNotes.rejected, (state, action) => {
                const teamId = action.meta.arg.teamId;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].notesLoading = false;
            })
            .addCase(createTeamNote.fulfilled, (state, action) => {
                const { teamId, note } = action.payload;
                if (state.sessions[teamId]) {
                    if (!Array.isArray(state.sessions[teamId].notes))
                        state.sessions[teamId].notes = [];
                    const exists = state.sessions[teamId].notes.some((n) => n.id === note.id);
                    if (!exists) {
                        state.sessions[teamId].notes.push(note);
                    }
                }
            })
            .addCase(updateTeamNote.fulfilled, (state, action) => {
                const { teamId, noteId, updates } = action.payload;
                const session = state.sessions[teamId];
                if (session) {
                    const n = session.notes.find((n) => n.id === noteId);
                    if (n) Object.assign(n, updates);
                }
            })
            .addCase(deleteTeamNote.fulfilled, (state, action) => {
                const { teamId, noteId } = action.payload;
                if (state.sessions[teamId]) {
                    state.sessions[teamId].notes = state.sessions[teamId].notes.filter(
                        (n) => n.id !== noteId,
                    );
                }
            });

        // -- Check-ins --
        builder
            .addCase(fetchTeamCheckins.pending, (state, action) => {
                const teamId = action.meta.arg;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].checkinsLoading = true;
            })
            .addCase(fetchTeamCheckins.fulfilled, (state, action) => {
                const { teamId, data } = action.payload;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].checkins = data;
                state.sessions[teamId].checkinsLoading = false;
            })
            .addCase(fetchTeamCheckins.rejected, (state, action) => {
                const teamId = action.meta.arg;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].checkinsLoading = false;
            })
            // createTeamCheckin / updateTeamCheckin / deleteTeamCheckin fulfilled handlers
            // intentionally have no state mutation — the backend emits checkins.created /
            // checkins.updated / checkins.deleted socket events after every write, and the
            // socket hook (addCheckin / updateCheckin / deleteCheckin sync reducers) is the
            // single source of truth for checkin state updates.
            .addCase(createTeamCheckin.fulfilled, () => { })
            .addCase(updateTeamCheckin.fulfilled, () => { })
            .addCase(deleteTeamCheckin.fulfilled, () => { });

        // -- RTC token --
        builder
            .addCase(fetchRtcToken.pending, (state, action) => {
                const teamId = action.meta.arg;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].rtcTokenLoading = true;
                state.sessions[teamId].rtcTokenError = null;
            })
            .addCase(fetchRtcToken.fulfilled, (state, action) => {
                const { teamId, rtcToken } = action.payload;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].rtcToken = rtcToken;
                state.sessions[teamId].rtcTokenLoading = false;
                state.sessions[teamId].rtcTokenError = null;
            })
            .addCase(fetchRtcToken.rejected, (state, action) => {
                const teamId = action.meta.arg;
                if (!state.sessions[teamId])
                    state.sessions[teamId] = createInitialTeamSessionState();
                state.sessions[teamId].rtcTokenLoading = false;
                state.sessions[teamId].rtcTokenError =
                    (action.payload as string) || "Failed to fetch RTC token";
            });
    },
});

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const {
    initTeamSession,
    updateTeamSessionState,
    addTask,
    toggleTask,
    editTask,
    deleteTask,
    addNote,
    editNote,
    deleteNote,
    addMember,
    removeMember,
    addCheckin,
    updateCheckin,
    deleteCheckin,
    toggleMemberMute,
} = teamFocusSessionSlice.actions;

export default teamFocusSessionSlice.reducer;

// Re-export types & thunks so consumers can import from this barrel
export type {
    Task,
    Note,
    TeamCheckin,
    TeamMember,
    TeamSessionState,
    TeamSessionsRootState,
    ApiTeamFocusSession,
    TeamFocusSessionParticipant,
} from "./types";
export { createInitialTeamSessionState } from "./types";
export { createTeamTask, updateTeamTask, fetchTeamTasks, deleteTeamTask } from "./taskThunks";
export {
    fetchTeamNotes,
    createTeamNote,
    updateTeamNote,
    deleteTeamNote,
} from "./noteThunks";
export {
    fetchTeamCheckins,
    createTeamCheckin,
    updateTeamCheckin,
    deleteTeamCheckin,
} from "./checkinThunks";
export {
    fetchTeamSession,
    fetchRtcToken,
    updateTeamTimer,
    endTeamSession,
    leaveTeamSession,
    participantJoinSession,
    participantLeaveSession,
} from "./sessionThunks";
